import type { OccupantEntry } from '../types/form';

/** Letters (Unicode), spaces, hyphens, apostrophes, periods — typical person names. */
const PERSON_NAME_RE = /^[\p{L}\s'.-]+$/u;

/** Pragmatic email check (optional field uses empty = valid). */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function validatePersonName(value: string): string | null {
  const t = value.trim();
  if (!t) return null;
  if (t.length < 2) return 'validation.name.minLength';
  if (!PERSON_NAME_RE.test(t)) {
    return 'validation.name.invalidChars';
  }
  return null;
}

/** Required phone: at least 10 digits. */
export function validatePhoneRequired(value: string): string | null {
  const t = value.trim();
  if (!t) return null;
  const digits = t.replace(/\D/g, '');
  if (digits.length < 10) return 'validation.phone.digits';
  if (digits.length > 15) return 'validation.phone.invalid';
  return null;
}

export function validateEmailOptional(value: string): string | null {
  const t = value.trim();
  if (!t) return null;
  if (!EMAIL_RE.test(t)) return 'validation.email.invalid';
  return null;
}

function parseIsoDateLocal(iso: string): Date | null {
  if (!ISO_DATE_RE.test(iso)) return null;
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function validateDateOfBirthOptional(value: string): string | null {
  const t = value.trim();
  if (!t) return null;
  const d = parseIsoDateLocal(t);
  if (!d) return 'validation.date.invalid';
  const now = new Date();
  if (d > now) return 'validation.date.dobFuture';
  if (d.getFullYear() < 1900) return 'validation.date.dobYear';
  return null;
}

export function validateIncidentDateOptional(value: string): string | null {
  const t = value.trim();
  if (!t) return null;
  const d = parseIsoDateLocal(t);
  if (!d) return 'validation.date.invalid';
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);
  if (d > endOfToday) return 'validation.date.accidentFuture';
  return null;
}

/** Single-field full name (occupant). */
export function validateOccupantName(value: string): string | null {
  return validatePersonName(value);
}

/** Optional free text (insurance, etc.): reasonable chars only. */
const LOOSE_TEXT_RE = /^[\p{L}\p{N}\s'.&,/#+\-/]+$/u;

export function validateOptionalInsuranceLine(value: string): string | null {
  const t = value.trim();
  if (!t) return null;
  if (t.length > 200) return 'validation.insurance.maxLength';
  if (!LOOSE_TEXT_RE.test(t)) {
    return 'validation.insurance.badChars';
  }
  return null;
}

export function validateAdditionalInfoOptional(value: string, maxLen = 10000): string | null {
  if (value.length > maxLen) {
    return 'validation.additionalInfo.maxLength';
  }
  return null;
}

export type ApplicationStepFieldKey =
  | 'first_name'
  | 'last_name'
  | 'phone_number'
  | 'email'
  | 'reporter_first_name'
  | 'reporter_last_name'
  | 'reporter_phone_number'
  | 'reporter_email'
  | 'date_of_birth'
  | 'incident_date';

export interface ApplicationStepValues {
  isPatient: boolean;
  first_name: string;
  last_name: string;
  phone_number: string;
  email: string;
  reporter_first_name: string;
  reporter_last_name: string;
  reporter_phone_number: string;
  reporter_email: string;
  date_of_birth: string;
  incident_date: string;
}

const nonEmptyTrim = (s: string): boolean => s.trim() !== '';

/** Whether required Step 1 fields are filled (Next disabled until true; empty fields do not show format errors). */
export function applicationStepRequiredFieldsFilled(v: ApplicationStepValues): boolean {
  if (v.isPatient) {
    return (
      nonEmptyTrim(v.first_name) &&
      nonEmptyTrim(v.last_name) &&
      nonEmptyTrim(v.phone_number) &&
      nonEmptyTrim(v.date_of_birth) &&
      nonEmptyTrim(v.incident_date)
    );
  }
  return (
    nonEmptyTrim(v.reporter_first_name) &&
    nonEmptyTrim(v.reporter_last_name) &&
    nonEmptyTrim(v.reporter_phone_number) &&
    nonEmptyTrim(v.first_name) &&
    nonEmptyTrim(v.last_name) &&
    nonEmptyTrim(v.phone_number) &&
    nonEmptyTrim(v.date_of_birth) &&
    nonEmptyTrim(v.incident_date)
  );
}

export function validateIncidentStepFields(input: {
  occupantList: OccupantEntry[];
  anotherVehicleInvolved: string;
  vehicle_insurance_name: string;
  vehicle_policy_number: string;
  vehicle_claim_number: string;
}): Record<string, string> {
  const e: Record<string, string> = {};
  input.occupantList.forEach((o, index) => {
    const ne = validateOccupantName(o.name);
    if (ne) e[`occ-${index}-name`] = ne;
    const pe = validatePhoneRequired(o.phone_number);
    if (pe) e[`occ-${index}-phone`] = pe;
  });
  if (input.anotherVehicleInvolved === 'yes') {
    const v1 = validateOptionalInsuranceLine(input.vehicle_insurance_name);
    if (v1) e['vehicle-insurance'] = v1;
    const v2 = validateOptionalInsuranceLine(input.vehicle_policy_number);
    if (v2) e['vehicle-policy'] = v2;
    const v3 = validateOptionalInsuranceLine(input.vehicle_claim_number);
    if (v3) e['vehicle-claim'] = v3;
  }
  return e;
}

export function validateApplicationStepFields(
  v: ApplicationStepValues
): Partial<Record<ApplicationStepFieldKey, string>> {
  const e: Partial<Record<ApplicationStepFieldKey, string>> = {};
  if (v.isPatient) {
    const fn = validatePersonName(v.first_name);
    if (fn) e.first_name = fn;
    const ln = validatePersonName(v.last_name);
    if (ln) e.last_name = ln;
    const ph = validatePhoneRequired(v.phone_number);
    if (ph) e.phone_number = ph;
    const em = validateEmailOptional(v.email);
    if (em) e.email = em;
    const dob = validateDateOfBirthOptional(v.date_of_birth);
    if (dob) e.date_of_birth = dob;
    const id = validateIncidentDateOptional(v.incident_date);
    if (id) e.incident_date = id;
  } else {
    const rfn = validatePersonName(v.reporter_first_name);
    if (rfn) e.reporter_first_name = rfn;
    const rln = validatePersonName(v.reporter_last_name);
    if (rln) e.reporter_last_name = rln;
    const rph = validatePhoneRequired(v.reporter_phone_number);
    if (rph) e.reporter_phone_number = rph;
    const rem = validateEmailOptional(v.reporter_email);
    if (rem) e.reporter_email = rem;
    const pfn = validatePersonName(v.first_name);
    if (pfn) e.first_name = pfn;
    const pln = validatePersonName(v.last_name);
    if (pln) e.last_name = pln;
    const pph = validatePhoneRequired(v.phone_number);
    if (pph) e.phone_number = pph;
    const pem = validateEmailOptional(v.email);
    if (pem) e.email = pem;
    const dob = validateDateOfBirthOptional(v.date_of_birth);
    if (dob) e.date_of_birth = dob;
    const ind = validateIncidentDateOptional(v.incident_date);
    if (ind) e.incident_date = ind;
  }
  return e;
}
